/* The editor's folder: every file as a tree, the open one marked, a problem the server
   found marked on its file. A path is where a file lives, so renaming one to
   `references/notes.md` is also how it moves; folders are just the paths' parts.
   SKILL.md always stays. */
import React, { useMemo, useRef, useState } from "react";
import { FilePlus, Folder, Pencil, Trash2, Upload } from "lucide-react";

import { FileTypeIcon } from "@/shared/components/icons/filetype";
import { IconButton } from "@/shared/components/ui-kit";
import { extension } from "@/shared/components/viewer/model";

import { buildTree, type FileNode } from "../catalog";
import { isKept, type DraftFile } from "../folder";

/** Why a path can't be used, if it can't — the server's rules, said before a save. */
export function pathIssue(path: string, taken: string[]): string | null {
  if (!path) return "Give it a name.";
  if (path.startsWith("/") || path.split("/").some((part) => part === ".." || part === "." || !part)) return "A path inside the folder, like references/notes.md.";
  if (/[\\\u0000-\u001f\u007f]/.test(path)) return "No backslashes or control characters in a path.";
  if (path === "SKILL.md" || taken.includes(path)) return "There's a file there already.";
  if (taken.some((t) => t.startsWith(`${path}/`))) return "There's a folder there already.";
  if (taken.some((t) => path.startsWith(`${t}/`))) return "A file has that folder's name.";
  if (!isKept(path)) return "Skills leave this name out: pick another.";
  return null;
}

function PathInput({ initial, taken, onDone, onCancel }: {
  initial: string;
  taken: string[];
  onDone: (path: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const path = value.trim();
  const issue = path && path !== initial ? pathIssue(path, taken) : null;
  return (
    <div className="px-2 py-1">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter") {
            e.preventDefault();
            if (path === initial) onCancel();
            else if (!pathIssue(path, taken)) onDone(path);
          }
        }}
        onBlur={onCancel}
        aria-label="File path"
        aria-invalid={!!issue}
        placeholder="references/notes.md"
        spellCheck={false}
        className={`w-full h-7 px-2 rounded-[8px] bg-[var(--surface)] ring-1 outline-none font-mono text-[16px] sm:text-[12px] ${issue ? "ring-[var(--danger-ring)]" : "ring-[var(--hairline-focus)]"}`}
      />
      {issue && <div className="mt-1 text-[11.5px] leading-[1.4] text-[var(--danger)]">{issue}</div>}
    </div>
  );
}

export default function EditorTree({ files, open, flagged, onOpen, onAdd, onUpload, onRename, onRemove }: {
  /** SKILL.md included. */
  files: DraftFile[];
  open: string;
  /** Paths the server found a problem with. */
  flagged: Set<string>;
  onOpen: (path: string) => void;
  onAdd: (path: string) => void;
  onUpload: (files: FileList) => void;
  onRename: (from: string, to: string) => void;
  onRemove: (path: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const tree = useMemo(() => buildTree(files.map((f) => ({ path: f.path, size: f.size, sha: "" }))), [files]);
  const taken = files.map((f) => f.path);

  const rows = (nodes: FileNode[], depth: number): React.ReactNode =>
    nodes.map((node) => {
      const pad = { paddingLeft: 10 + depth * 14 };
      if (node.children) {
        return (
          <div key={node.path}>
            <div className="flex items-center gap-1.5 pr-2 py-[5px] text-[12.5px] text-[var(--text-tertiary)]" style={pad}>
              <Folder className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
              <span className="truncate">{node.name}</span>
            </div>
            {rows(node.children, depth + 1)}
          </div>
        );
      }
      if (renaming === node.path) {
        return (
          <PathInput
            key={node.path}
            initial={node.path}
            taken={taken.filter((p) => p !== node.path)}
            onCancel={() => setRenaming(null)}
            onDone={(to) => { setRenaming(null); onRename(node.path, to); }}
          />
        );
      }
      const active = node.path === open;
      const fixed = node.path === "SKILL.md";
      return (
        <div
          key={node.path}
          className={`group flex items-center gap-1.5 pr-1 rounded-[8px] mx-1 ${active ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"}`}
        >
          <button
            onClick={() => onOpen(node.path)}
            aria-current={active ? "true" : undefined}
            title={node.path}
            className={`press flex-1 min-w-0 flex items-center gap-1.5 py-[5px] text-left text-[12.5px] ${
              active ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
            style={{ paddingLeft: pad.paddingLeft - 4 }}
          >
            <FileTypeIcon kind={extension(node.path)} size={16} />
            <span className="truncate">{node.name}</span>
            {flagged.has(node.path) && <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-[var(--danger)]" aria-label="has a problem" />}
          </button>
          {!fixed && (
            <span className="hidden group-hover:flex group-focus-within:flex items-center">
              <IconButton onClick={() => setRenaming(node.path)} title={`Rename or move ${node.name}`} aria-label={`Rename ${node.name}`}>
                <Pencil className="w-3 h-3" strokeWidth={1.75} />
              </IconButton>
              <IconButton onClick={() => onRemove(node.path)} title={`Remove ${node.name}`} aria-label={`Remove ${node.name}`} className="hover:text-[var(--danger)]">
                <Trash2 className="w-3 h-3" strokeWidth={1.75} />
              </IconButton>
            </span>
          )}
        </div>
      );
    });

  return (
    <nav aria-label="Files" className="md:w-[240px] shrink-0 flex flex-col min-h-0 max-h-[220px] md:max-h-none bg-[var(--surface-sunk)] border-b md:border-b-0 md:border-r hairline">
      <div className="h-10 shrink-0 flex items-center gap-1 pl-3 pr-1.5">
        <span className="section-label flex-1">Files</span>
        <IconButton onClick={() => setAdding(true)} title="New file" aria-label="New file">
          <FilePlus className="w-3.5 h-3.5" strokeWidth={1.75} />
        </IconButton>
        <IconButton onClick={() => picker.current?.click()} title="Upload files into the folder" aria-label="Upload files">
          <Upload className="w-3.5 h-3.5" strokeWidth={1.75} />
        </IconButton>
        <input
          ref={picker}
          type="file"
          multiple
          className="hidden"
          aria-label="Upload files into the folder"
          onChange={(e) => { if (e.target.files?.length) onUpload(e.target.files); e.target.value = ""; }}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pb-2">
        {adding && (
          <PathInput initial="" taken={taken} onCancel={() => setAdding(false)} onDone={(path) => { setAdding(false); onAdd(path); }} />
        )}
        {rows(tree, 0)}
      </div>
    </nav>
  );
}
